// promptTemplates/index.js 
import { useState, useEffect } from 'react';


// Import all prompt templates - make sure these paths are correct!
import {
  MAIN_TOUR_PROMPT,
  STOP_DETAIL_PROMPT,
  HISTORICAL_CONTEXT_PROMPT_KNOWLEDGE,
  CULTURAL_SIGNIFICANCE_PROMPT_KNOWLEDGE,
  HIDDEN_GEMS_PROMPT,
  NARRATIVE_STRUCTURE_ADDENDUM,
  DESCRIPTION_PROMPT  
} from './regularPrompts.js'; // Add .js extension

import {
  SASSY_MAIN_TOUR_PROMPT,
  SASSY_STOP_DETAIL_PROMPT,
  SASSY_HISTORICAL_CONTEXT_PROMPT_KNOWLEDGE,
  SASSY_CULTURAL_SIGNIFICANCE_PROMPT_KNOWLEDGE,
  SASSY_HIDDEN_GEMS_PROMPT,
  SASSY_NARRATIVE_STRUCTURE_ADDENDUM,
  SASSY_DESCRIPTION_PROMPT  
} from './sassyPrompts.js'; // Add .js extension

// Define available voice styles
export const VOICE_STYLES = [
  
  { id: 'sassy', name: 'Sassy Sofia', description: 'Bold, irreverent and authentic local friend style' },
  { id: 'regular', name: 'Regular Guide', description: 'Professional and informative tour guide style' }
];

// Fill placeholders in any prompt template
export const fillPromptTemplate = (template, data) => {
  let filledTemplate = template;
  
  // Only replace placeholders that exist in the data object
  if (data.location) filledTemplate = filledTemplate.replace(/\[LOCATION\]/g, data.location);
  if (data.city && data.country) {
    filledTemplate = filledTemplate.replace(/\[LOCATION\]/g, `${data.city}, ${data.country}`);
  }
  if (data.theme) filledTemplate = filledTemplate.replace(/\[THEME\]/g, data.theme);
  if (data.audience) filledTemplate = filledTemplate.replace(/\[AUDIENCE\]/g, data.audience);
  if (data.stopName) filledTemplate = filledTemplate.replace(/\[STOP_NAME\]/g, data.stopName);
  if (data.context) filledTemplate = filledTemplate.replace(/\[CONTEXT\]/g, data.context);
  if (data.stops) filledTemplate = filledTemplate.replace(/\[STOPS\]/g, data.stops);
  
  return filledTemplate;
};

// Create a tour continuity section with previous POIs
export const buildTourContinuitySection = (poi, itineraryPoints, itineraryMetadata) => {
  // Filter to get only existing POIs that aren't the current POI
  const existingPois = itineraryPoints
    .filter(point => !poi.id || point.id !== poi.id)
    .filter(point => point.description || point.inDepthDescription);
  
  if (existingPois.length === 0) return '';
  
  // Find the position of the current POI in the itinerary
  const currentPoiIndex = poi.id 
    ? itineraryPoints.findIndex(point => point.id === poi.id)
    : itineraryPoints.length;
  
  // Calculate current position in the itinerary (not directly used in the prompt string)
  
  let continuitySection = `\n\nCRITICAL - TOUR CONTINUITY AND BUILDING ON PREVIOUS INFORMATION:

You can also find here all the other descriptions of our tour that we built so far (more might come). 
For this stop our tour you must: 
1. DO NOT repeat information already covered at previous stops  
 

OTHER STOPS IN TOUR:`;
  
  // Get all other POIs in the itinerary order, not just the ones before
  const otherPois = itineraryPoints
    .filter(point => !poi.id || point.id !== poi.id)
    .filter(point => point.description || point.inDepthDescription);
  
  // Add full details of each other POI in their current order
  otherPois.forEach((poi, index) => {
    // Find the actual position of this POI in the full itinerary
    const actualPosition = itineraryPoints.findIndex(p => p.id === poi.id) + 1;
    
    continuitySection += `\n\n======= STOP #${actualPosition}: ${poi.name} (${poi.category || 'No category'}) =======\n`;
  
    continuitySection += `IN-DEPTH DESCRIPTION: ${poi.inDepthDescription || "Not provided"}\n\n`; 
    
     
  });
  
  // Additional guidance for creating continuity
  continuitySection += `\n\nThis stop (${poi.name || 'Current location'}) is the next part of our narrative. Your description should:

- Add new information without repeating what was already covered
- Advance the overall theme of "${itineraryMetadata.theme || ''}" 
  `;
  
  return continuitySection;
};

// Build a complete prompt with all required sections
export const buildCompletePrompt = (basePrompt, data, options = {}) => {
  const { 
    includeNarrativeStructure = true,
    includeTourContinuity = true,
    itineraryPoints = [], 
    poi = {},
    narrativeStructure
  } = options;
  
  // Start with the base prompt filled with data
  let completePrompt = fillPromptTemplate(basePrompt, data);
  
  // Add narrative structure if requested
  if (includeNarrativeStructure && narrativeStructure) {
    completePrompt += '\n\n' + narrativeStructure;
  }
  
  // Add tour continuity if requested and there are previous POIs
  if (includeTourContinuity && itineraryPoints.length > 0) {
    completePrompt += buildTourContinuitySection(poi, itineraryPoints, data);
  }
  
  return completePrompt;
};

// Hook for managing prompt templates with state
 // Hook for managing prompt templates with state
export const usePromptTemplates = (currentVoiceStyle) => {
  const [internalVoiceStyle, setInternalVoiceStyle] = useState(currentVoiceStyle || 'regular');
  
  // Update internal state when external state changes
  useEffect(() => {
    if (currentVoiceStyle) {
      setInternalVoiceStyle(currentVoiceStyle);
    }
  }, [currentVoiceStyle]);
  
  // Get the appropriate prompt based on voice style
  const getPromptForVoice = (promptType) => {
    if (internalVoiceStyle === 'sassy') {
      switch (promptType) {
        case 'description':
          return SASSY_DESCRIPTION_PROMPT;
        case 'main':
          return SASSY_MAIN_TOUR_PROMPT;
        case 'stopDetail':
          return SASSY_STOP_DETAIL_PROMPT;
        case 'historicalContext':
          return SASSY_HISTORICAL_CONTEXT_PROMPT_KNOWLEDGE;
        case 'culturalSignificance':
          return SASSY_CULTURAL_SIGNIFICANCE_PROMPT_KNOWLEDGE;
        case 'hiddenGems':
          return SASSY_HIDDEN_GEMS_PROMPT;
        case 'narrativeStructure':
          return SASSY_NARRATIVE_STRUCTURE_ADDENDUM;
        default:
          return '';
      }
    } else {
      // Regular voice
      switch (promptType) {
        case 'description':
          return DESCRIPTION_PROMPT;
        case 'main':
          return MAIN_TOUR_PROMPT;
        case 'stopDetail':
          return STOP_DETAIL_PROMPT;
        case 'historicalContext':
          return HISTORICAL_CONTEXT_PROMPT_KNOWLEDGE;
        case 'culturalSignificance':
          return CULTURAL_SIGNIFICANCE_PROMPT_KNOWLEDGE;
        case 'hiddenGems':
          return HIDDEN_GEMS_PROMPT;
        case 'narrativeStructure':
          return NARRATIVE_STRUCTURE_ADDENDUM;
        default:
          return '';
      }
    }
  };

  // Generate complete filled prompts for different fields
// Replace the generatePromptForField function in index_prompt_templates.js with this:

const generatePromptForField = (field, poi, itineraryMetadata, itineraryPoints = []) => {
  let basePrompt = '';
  let data = {
    location: itineraryMetadata.location,
    city: itineraryMetadata.city,
    country: itineraryMetadata.country,
    theme: itineraryMetadata.theme,
    audience: itineraryMetadata.targetAudience,
    stopName: poi.name
  };
  
  switch (field) {
    case 'description': {
      const languageMap = {
        'pt-PT': 'Write in European Portuguese (Portugal). ',
        'pt-BR': 'Write in Brazilian Portuguese. ',
        'es-ES': 'Write in Castilian Spanish. ',
        'es-MX': 'Write in Mexican Spanish. ',
        'en-US': 'Write in American English. ',
        'en-GB': 'Write in British English. ',
        'fr-FR': 'Write in French. ',
      };
      const lang = itineraryMetadata.language || 'en-US';
      const langPrefix = languageMap[lang] || '';
      basePrompt = langPrefix + getPromptForVoice('description');
      break;
    }
    case 'inDepthDescription':
      basePrompt = getPromptForVoice('stopDetail');
      data.context = poi.inDepthDescriptionContext || '';
      break;
    case 'historicalContext':
    case 'culturalSignificance':
    case 'hiddenGems': {
      const languageMap = {
        'pt-PT': 'Write in European Portuguese (Portugal). ',
        'pt-BR': 'Write in Brazilian Portuguese. ',
        'es-ES': 'Write in Castilian Spanish. ',
        'es-MX': 'Write in Mexican Spanish. ',
        'en-US': 'Write in American English. ',
        'en-GB': 'Write in British English. ',
        'fr-FR': 'Write in French. ',
      };
      const lang = itineraryMetadata.language || 'en-US';
      const langPrefix = languageMap[lang] || '';
      basePrompt = langPrefix + getPromptForVoice(field);
      break;
    }
    case 'keywords':
      basePrompt = `Generate a list of 15 keywords or key phrases CLOSELY related to this stop: ${poi.name}, a ${poi.category}  in ${itineraryMetadata.city}, ${itineraryMetadata.country}.
Focus on names, places, events, and themes that are relevant to this location. 
These keywords HAVE to be themes I can use to enquire further about: for example: "Architecture" would be ok,  "subtlety"  would not. 
Likely generic keywords like "Lisbon" also are not good. It is imperative that you do not provide me generic keywords not related with this stop.

Return ONLY a comma-separated list without any additional text or formatting.`;
      break;
    case 'tourPlan':
      basePrompt = getPromptForVoice('main');
      data.stops = itineraryMetadata.numberOfStops || '';
      break;
    default:
      basePrompt = `Generate content for the ${field} field of ${poi.name || 'this point of interest'}. Return only the content without any formatting or additional text.`;
  }
  
  const finalPrompt = buildCompletePrompt(basePrompt, data, {
    includeNarrativeStructure: ['inDepthDescription'].includes(field),
    includeTourContinuity: ['inDepthDescription'].includes(field),
    itineraryPoints,
    poi,
    narrativeStructure: getPromptForVoice('narrativeStructure')
  });
  
  return finalPrompt;
};

  return {
    voiceStyle: internalVoiceStyle,
    setVoiceStyle: setInternalVoiceStyle,
    getPromptForVoice,
    generatePromptForField
  };
};

// Export all the prompt templates
export {
  MAIN_TOUR_PROMPT,
  STOP_DETAIL_PROMPT,
  HISTORICAL_CONTEXT_PROMPT_KNOWLEDGE,
  CULTURAL_SIGNIFICANCE_PROMPT_KNOWLEDGE,
  HIDDEN_GEMS_PROMPT,
  NARRATIVE_STRUCTURE_ADDENDUM,
  DESCRIPTION_PROMPT,  // Add this
  SASSY_MAIN_TOUR_PROMPT,
  SASSY_STOP_DETAIL_PROMPT,
  SASSY_HISTORICAL_CONTEXT_PROMPT_KNOWLEDGE,
  SASSY_CULTURAL_SIGNIFICANCE_PROMPT_KNOWLEDGE,
  SASSY_HIDDEN_GEMS_PROMPT,
  SASSY_NARRATIVE_STRUCTURE_ADDENDUM,
  SASSY_DESCRIPTION_PROMPT  // Add this
};
